import { format } from "date-fns";
import React from "react";
import { formatReadableTimeSlot } from "../../../services/TimesheetService";
import { TTimesheetVacancyData } from "../../../store/timesheet/types";
import Break from "../../UI/Icons/Break";
import LogIn from "../../UI/Icons/LogIn";
import LogOut from "../../UI/Icons/LogOut";
import PopupModal from "../../UI/Popup";
import CentreImage from "../../Vacancy/CentreImage";

interface ViewTimeSheetProps {
  /**
   * Modal visibility state
   */
  showViewTimesheetModal: boolean;
  /**
   * Function to set the visibility state of the modal
   * @param value 
   * @returns 
   */
  setShowViewTimesheetModal: (value: boolean) => void;
  /**
   * Data for the timesheet vacancy to be displayed
  * @type {TTimesheetVacancyData | null}
   */
  timeSheetData?: TTimesheetVacancyData | null;
  /**
   * Function to get formatted time from date string
   * @param job_start_time 
   * @param job_end_time 
   * @returns 
   */
  formatJobTime?: (
    job_start_time: string | null,
    job_end_time: string | null
  ) => string;
  /**
   * Function to get time from date string
   * @param date 
   * @returns 
   */
  getTimeFromDate: (date?: string | null) => string;
  /**
   * Function to get date from date string
   * @param dateString 
   * @returns 
   */
  getDateFromDateString?: (dateString: string) => string;
  /**
   * Function to get UTC formatted date and time
   * @param datestr 
   * @param timestr 
   * @returns 
   */
  getUtcFormatDateTime?: (
    datestr: string | null,
    timestr: string | null
  ) => string;
}



/**
 * Displays a modal popup to view the details of a candidate's submitted timesheet for a specific job.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.showViewTimesheetModal - Controls the visibility of the timesheet modal.
 * @param {(visible: boolean) => void} props.setShowViewTimesheetModal - Function to update the modal visibility state.
 * @param {any} props.timeSheetData - The timesheet data object containing job, centre, and time details.
 * @param {(date: string | null) => string} props.getTimeFromDate - Function to extract and format the time from a date string.
 * @param {(start: string | null, end: string | null) => string} props.formatJobTime - Function to format the job's timeframe.
 * @param {(dateString: string) => Date | null} props.getDateFromDateString - Function to parse a date string into a Date object.
 * @param {(date: Date | null, time: string | null) => string} props.getUtcFormatDateTime - Function to format a date and time into a UTC string.
 *
 * @returns {JSX.Element} The rendered timesheet view modal.
 *
 * @remarks
 * - Shows job, centre, and timeframe details, as well as the candidate's submitted start, end, and break times.
 * - Uses responsive layouts for desktop and mobile views.
 * - Displays a status indicator and allows closing the modal.
 */
const ViewTimeSheet: React.FC<ViewTimeSheetProps> = ({
  showViewTimesheetModal,
  setShowViewTimesheetModal,
  timeSheetData,
  getTimeFromDate,
  formatJobTime,
  getDateFromDateString,
  getUtcFormatDateTime,
}) => {
  const onClickClose = () => {
    setShowViewTimesheetModal(false);
  };


  // If timeSheetData is not provided, return null to avoid rendering the modal
  const jobDate =
    getDateFromDateString &&
    getDateFromDateString(timeSheetData?.job_start_time ?? "");

  // If jobDate is not provided, use the same date as jobDate
  const jobEndDate =
    getDateFromDateString &&
    getDateFromDateString(timeSheetData?.job_start_time ?? "");
  
  // If jobDate is not provided, use the same date as jobDate
  const jobStartDateTime =
    getUtcFormatDateTime &&
    getUtcFormatDateTime(
      jobDate ?? null,
      timeSheetData?.timeframe?.start_time ?? null
    );

  // If jobEndDate is not provided, use the same date as jobDate
  const jobEndDateTime =
    getUtcFormatDateTime &&
    getUtcFormatDateTime(
      jobEndDate ?? null,
      timeSheetData?.timeframe?.end_time ?? null
    );

  // Format the job timeframe using the provided function
  const jobTimeFrame =
    formatJobTime &&
    formatJobTime(jobStartDateTime ?? null, jobEndDateTime ?? null);

  return (
    <>
      <PopupModal
        visible={showViewTimesheetModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={650}
        popUpClass="mobile-model"
      >
        <div className="d-flex flex-column gap-15">
          <div className="fs-22 fw-400 text-black">View Timesheet</div>
          <div className="bg-off-white h-1px"></div>
          <div className="d-flex flex-column gap-15 mobile-job-scroll">
            <div className="d-flex gap-20">
              <div className="w-50px h-50px  border-2 border-grey-dark radius-40px">
                <CentreImage
                  imageUrl={timeSheetData?.centre?.photo ?? undefined}
                  width="50px"
                  height="50px"
                  isCandidateFilledAllForms={true}
                />
              </div>
              <div className="flex-grow-1 d-flex flex-column gap-20">
                <div className="flex-grow-1 d-flex flex-column gap-3 text-break">
                  <div className="fs-16 fw-700 text-black">
                    {timeSheetData?.centre?.business_name},{" "}
                    <span className="fw-400">
                      {timeSheetData?.centre?.address}
                    </span>
                  </div>
                  <div className="fs-13 fw-400 text-black">
                    {timeSheetData?.vacancy?.matching_position?.name ?? ""}
                  </div>
                </div>
                <div className="d-flex flex-column gap-3 fs-13 text-black">
                  <div className="fw-700">Job Timeframe</div>
                  <div className="fw-400">
                    { timeSheetData !== null ? formatReadableTimeSlot(
                      format(new Date(timeSheetData?.job_start_time ?? ""), "yyyy-MM-dd"), 
                      timeSheetData?.timeframe?.start_time || "", 
                      timeSheetData?.timeframe?.end_time || "")
                    : ""}
                    {/* {jobTimeFrame ? jobTimeFrame : "Not Available"} */}
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex gap-10 align-items-center">
              <div className="fs-16 fw-700 text-black">Status:</div>
              <div className="border border-alert radius-8px px-8px py-4px fs-13 fw-400 text-alert">
                Pending Approval
              </div>
            </div>
            <div className="h-1px"></div>
            <div className="fs-16 fw-700 text-black">
              Your submitted details for the above job:
            </div>

            <div className="d-flex gap-50">
              <div className="d-flex gap-10 align-items-center">
                <div className="d-none d-lg-block">
                  <LogIn />
                </div>
                <div className="d-flex flex-column gap-5">
                  <div className="fs-13 fw-400 text-grey-medium">
                    <span className="d-none d-lg-block">Job start time</span>
                    <span className="d-block d-lg-none">Start time</span>
                  </div>
                  <div className="fs-16 fw-400 text-black">
                    {getTimeFromDate &&
                      getTimeFromDate(timeSheetData?.job_start_time ?? null)}
                  </div>
                </div>
              </div>
              <div className="d-none d-lg-flex gap-10 align-items-center">
                <div className="d-none d-lg-block">
                  <Break />
                </div>
                <div className="d-flex flex-column gap-5">
                  <div className="fs-13 fw-400 text-grey-medium">
                    Break timeframe
                  </div>
                  <div className="fs-16 fw-400 text-black">
                    {timeSheetData?.break_start_time === null &&
                    timeSheetData?.break_end_time === null
                      ? "-"
                      : getTimeFromDate(
                          timeSheetData?.break_start_time ?? null
                        ) +
                        " to " +
                        getTimeFromDate(timeSheetData?.break_end_time ?? null)}
                  </div>
                </div>
              </div>
              <div className="d-flex gap-10 align-items-center">
                <div className="d-none d-lg-block">
                  <LogOut color="#167E76" />
                </div>
                <div className="d-flex flex-column gap-5">
                  <div className="fs-13 fw-400 text-grey-medium">
                    <span className="d-none d-lg-block">Job end time</span>
                    <span className="d-block d-lg-none">End time</span>
                  </div>
                  <div className="fs-16 fw-400 text-black">
                    {getTimeFromDate(timeSheetData?.job_end_time ?? null)}
                  </div>
                </div>
              </div>
              {/* Mobile Screen Break Format */}
              <div className="d-flex d-lg-none flex-column gap-5">
                <div className="fs-13 fw-400 text-grey-medium">Break</div>
                <div className="fs-16 fw-400 text-black">45min</div>
              </div>
            </div>
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default ViewTimeSheet;
